import * as React from 'react';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import Invoices from './Invoices';
import type { InvoiceDetails, InvoiceItems } from './models/InvoiceInterfaces';
import { DownloadFile, GetQuery, PostQuery } from './Shared';
import { useNavigate } from 'react-router-dom';

interface BatchNumber {
    id: number;
    number: number;
    status: string;
    statusId: number;
    description: string;
    isEvent: boolean;
    rowVersionTimestamp: Uint8Array;
    sentDateFormatted: string;
}

const InvoiceBatchData = () => {
    let navigate = useNavigate();
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [loading, setLoading] = React.useState(true);
    const [loadingInvoices, setLoadingInvoices] = React.useState(false);
    const [locking, setLocking] = React.useState(false);
    const [sending, setSending] = React.useState(false);
    const [lockBatchError, setLockBatchError] = React.useState("");
    const [sendBatchError, setSendBatchError] = React.useState("");
    const [selectedBatch, setSelectedBatch] = React.useState({ batchId: -1, batchNumber: 0, batchStatusId: 0, isEvent: false, sentDateFormatted: "" });
    const [moveInvoices, setMoveInvoices] = React.useState({ fromDate: "", toDate: "" });
    const [rvt, setrvt] = React.useState<Uint8Array>();
    const [batchNumbers, setBatchNumbers] = React.useState<BatchNumber[]>([]);
    const [showOpenBatches, setShowOpenBatches] = React.useState(false);
    const [invoices, setInvoices] = React.useState<InvoiceDetails[]>([]);
    const [invoiceTotalFormatted, setInvoiceTotalFormatted] = React.useState("$0.00");
    const [totalRecords, setTotalRecords] = React.useState(0);
    const [canEdit, setCanEdit] = React.useState(false);
    const [tab, setTab] = React.useState("Batch");
    const [batchSending, setBatchSending] = React.useState(false);
    const [invoiceSending, setInvoiceSending] = React.useState(false);
    const [sendEventInvoicesOnly, setSendEventInvoicesOnly] = React.useState(false);
    const [eventBatchId, setEventBatchId] = React.useState(0);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const apiUrl = "CheckInvoiceBatchPermission";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            var canEditData = result.data.canEdit;
            var canSendBatch = result.data.viewSendInvoiceBatch;
            var canSendEventInvoices = result.data.sendEventInvoices;

            if (canSendBatch || canSendEventInvoices) {
                setCanEdit(canEditData)
                setSendEventInvoicesOnly(canSendEventInvoices);
                setEventBatchId(result.data.eventInvoiceBatchId);
                //enter the page that quickbooks needs to return to, id blank as not used via invoicing
                var page = {
                    page: "invoicebatch",
                    id: 0
                };
                //encode to base 64 so can retrieve it later
                var encoded = window.btoa(JSON.stringify(page));

                checkReckonAuthorised(encoded, canSendEventInvoices);
            } else {
                //redirect to 403 permission denied
                navigate("/accessdenied");
            }
        };
    }

    const checkReckonAuthorised = async (encodedPage: string, canSendEventInvoices: boolean) => {
        const apiUrl = "ReckonInvoiceAuthoriseCheck?returnUrl=" + encodedPage;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            //continue if Quick Books user is authorised
            if (result.data.isAuthorised) {
                getBatchNumbers(true, -1, false, canSendEventInvoices);
            } else {
                window.location.replace(result.data.reckonLoginUrl)
            }
        };
    }

    const getBatchNumbers = async (getInvoices: boolean, existingBatchId: number, showOpen: boolean, eventInvoicesOnly: boolean) => {
        const apiUrl = "GetBatchNumbers?ShowOpen=" + showOpen + '&EventInvoicesOnly=' + eventInvoicesOnly;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            let batchBeingSent = result.data.batchBeingSentToReckon;
            let invoiceBeingSent = result.data.invoiceBeingSentToReckon;
            if (batchBeingSent || invoiceBeingSent) {
                setLoading(false);
                setBatchSending(batchBeingSent);
                setInvoiceSending(invoiceBeingSent);
            } else {

                let batchNos: BatchNumber[] = result.data.invoiceBatchNumbers;

                var updatedBatchId = -1;
                var updatedBatchNo = 0;
                var batchStatus = 0;
                var rowVersion;
                var isEvent = false;
                var sentDate = "";
                if (existingBatchId >= 0) {
                    updatedBatchId = existingBatchId;
                    //find the batch status
                    var batchOption = batchNos.find(b => b.id == existingBatchId);
                    if (batchOption) {
                        batchStatus = batchOption.statusId;
                        rowVersion = batchOption.rowVersionTimestamp;
                        isEvent = batchOption.isEvent;
                        sentDate = batchOption.sentDateFormatted;
                        updatedBatchNo = batchOption.number;
                    }
                } else if (batchNos.length > 0 && getInvoices) {
                    var batchNo = batchNos[0];
                    updatedBatchId = batchNo.id;
                    batchStatus = batchNo.statusId;
                    rowVersion = batchNo.rowVersionTimestamp;
                    isEvent = batchNo.isEvent;
                    sentDate = batchNo.sentDateFormatted;
                    updatedBatchNo = batchNo.number;
                }

                setTab("Batch");
                setBatchNumbers(batchNos);
                setSelectedBatch({ batchId: updatedBatchId, batchNumber: updatedBatchNo, batchStatusId: batchStatus, isEvent: isEvent, sentDateFormatted: sentDate });
                setrvt(rowVersion);
                setLoading(false);
                setBatchSending(false);
                setInvoiceSending(false);
                setLockBatchError("");
                setSendBatchError("");

                if (batchNos.length > 0 && getInvoices) {
                    setLoadingInvoices(true);
                    loadInvoices(updatedBatchId, batchStatus);
                }
            }
        };
    }

    const loadInvoices = async (updatedBatchId: number, updatedBatchStatus: number) => {
        const apiUrl = "GetBatchInvoices?batchId=" + updatedBatchId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            let batchBeingSent = result.data.batchBeingSentToReckon;
            let invoiceBeingSent = result.data.invoiceBeingSentToReckon;
            if (batchBeingSent || invoiceBeingSent) {
                setLoading(false);
                setBatchSending(batchBeingSent);
                setInvoiceSending(invoiceBeingSent);
            } else {
                var invoiceDetails = result.data.invoices;

                for (var i = 0; i < invoiceDetails.length; i++) {
                    var invoice = invoiceDetails[i];

                    for (var y = 0; y < invoice.items.length; y++) {
                        invoice.items[y].itemIndex = y;
                        invoice.items[y].amount = invoice.items[y].amount.toFixed(2);
                    }
                    //add blank row if canEdit invoice and user can edit and open batch
                    if (invoice.canEdit && canEdit && updatedBatchStatus === 1) {
                        var newRow: InvoiceItems = {
                            itemIndex: invoice.items.length,
                            id: 0,
                            jobInvoiceId: invoice.id,
                            branchIdent: "",
                            item: "",
                            itemId: -1,
                            itemTypeId: -1,
                            invItemType: "",
                            invItemTypeId: -1,
                            quantityType: "",
                            purchaseOrderNumber: "",
                            additionalHireRate: "0.00",
                            rate: "0.00",
                            rateFormatted: "$0.00",
                            gst: "0.00",
                            gstFormatted: "$0.00",
                            chargeGst: true,
                            amount: "",
                            amountFormatted: "$0.00",
                            description: "",
                            status: 1,
                            invoiceStartDate: "",
                            invoiceEndDate: "",
                            invoiceStartDateFormatted: "",
                            invoiceEndDateFormatted: "",
                            hirePeriodDays: "",
                            hirePeriod: "",
                            dayRate: "",
                            weekMonthRate: "",
                            isDirty: false
                        }
                        invoice.items.push(newRow);
                    }
                }
                setInvoices(invoiceDetails);
                setInvoiceTotalFormatted(result.data.invoiceTotalFormatted);
                setTotalRecords(result.data.totalRecords);
                setLoading(false);
                setLoadingInvoices(false);
                setLocking(false);
                setBatchSending(false);
                setInvoiceSending(false);
                setLockBatchError("");
                setSendBatchError("");
            }
        };
    }

    const getErrorInvoices = async () => {
        const apiUrl = "GetSentErrorInvoices";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            var invoiceDetails = result.data.invoices;
            for (var i = 0; i < invoiceDetails.length; i++) {
                var invoice = invoiceDetails[i];
                invoice.errors = {};

                for (var y = 0; y < invoice.items.length; y++) {
                    invoice.items[y].itemIndex = y;
                }
            }

            setTab("Error");
            setInvoices(invoiceDetails);
            setTotalRecords(result.data.totalRecords);
            setLoading(false);
            setLoadingInvoices(false);
        };
    }

    const updateInvoiceDetails = (updatedInvoices: InvoiceDetails[]) => {
        setInvoices(updatedInvoices);
    }

    const tabSelected = (tab: string) => {
        if (tab == "Batch") {
            getBatchNumbers(true, -1, false, sendEventInvoicesOnly);
        } else {
            getErrorInvoices();
        }
    }

    const tabChanged = (e: any) => {
        tabSelected(e.target.value);
    }

    const noChange = (_e: any) => {
        //no changes as user doesn't actually click on the radio button
    }

    const save = (updatedInvoices: InvoiceDetails[]) => {
        if (updatedInvoices.length > 0) {
            saveInvoice(updatedInvoices);
        } else {
            toast.info("No invoices have been changed");
        }
    }

    const saveInvoice = async (invoiceDetails: InvoiceDetails[]) => {
        var invoiceitems = [];
        for (var inv = 0; inv < invoiceDetails.length; inv++) {
            var item = {
                jobId: invoiceDetails[inv].jobId,
                customerId: invoiceDetails[inv].customerId,
                invoiceItems: invoiceDetails[inv].items.filter(i => i.isDirty)
            }
            invoiceitems.push(item);
        }

        var invDetails = {
            invoiceDetails: invoiceitems
        }

        var jsonItem = JSON.stringify(invDetails);

        const apiUrl = "SaveInvoiceItems";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            setLoadingInvoices(true);
            loadInvoices(selectedBatch.batchId, selectedBatch.batchStatusId);
            toast.success("Invoices Saved");
        };
    }

    const handleBatchChange = (e: any) => {
        var updatedBatchId = e.target.value * 1;
        var batchOption = batchNumbers.find(b => b.id === updatedBatchId);
        setMoveInvoices(prevState => ({ ...prevState, fromDate: "", toDate: "" }));
        setErrors({});
        if (batchOption) {
            setSelectedBatch({ batchId: updatedBatchId, batchNumber: batchOption.number, batchStatusId: batchOption.statusId, isEvent: batchOption.isEvent, sentDateFormatted: batchOption.sentDateFormatted });
            setrvt(batchOption.rowVersionTimestamp);
            setLoadingInvoices(true);

            loadInvoices(e.target.value, batchOption.statusId);
        } else {
            setSelectedBatch({ batchId: -1, batchNumber: 0, batchStatusId: 0, isEvent: false, sentDateFormatted: "" });
            var timestamp = new Uint8Array();
            setrvt(timestamp);
            setInvoices([]);
            setLockBatchError("");
            setSendBatchError("");
        }
    }

    const handleShowOpenCheckboxChange = (e: any) => {
        setShowOpenBatches(e.target.checked)
        setInvoices([]);
        setInvoiceTotalFormatted("$0.00");
        setMoveInvoices(prevState => ({ ...prevState, fromDate: "", toDate: "" }));
        setErrors({});

        getBatchNumbers(false, -1, e.target.checked, sendEventInvoicesOnly);
    }

    const handleMoveDateChange = (e: any) => {
        setMoveInvoices(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    //download batch to Excel
    const downloadBatch = (e: any) => {
        e.preventDefault();

        downloadBatchToExcel();
    }

    const downloadBatchToExcel = async () => {
        const apiUrl = "ExportInvoiceBatchById?BatchId=" + selectedBatch.batchId;
        var fileName = "Invoice Batch " + selectedBatch.batchNumber + ".xlsx";
        await DownloadFile(apiUrl, fileName);
    }
    //end download batch to excel

    //move invoices into an open batch
    const validateMoveInvoices = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (!moveInvoices.fromDate) {
            formIsValid = false;
            errorList["fromDate"] = "From Date is required";
        } else if (!DateHelpers.isValidDate(moveInvoices.fromDate)) {
            formIsValid = false;
            errorList["fromDate"] = "Valid From Date is required";
        }

        if (!moveInvoices.toDate) {
            formIsValid = false;
            errorList["toDate"] = "To Date is required";
        } else if (!DateHelpers.isValidDate(moveInvoices.toDate)) {
            formIsValid = false;
            errorList["toDate"] = "Valid To Date is required";
        } else if (!DateHelpers.compareDate(moveInvoices.fromDate, moveInvoices.toDate)) {
            formIsValid = false;
            errorList["toDate"] = "To Date >= From Date";
        }

        setErrors(errorList);
        return formIsValid;
    }

    const moveInvoicesToNewBatch = () => {
        if (validateMoveInvoices()) {
            moveInvoicesToBatch();
        } else {
            toast.error("Fix validation errors before moving invoices to a new batch!");
        }
    }

    const moveInvoicesToBatch = async () => {
        var moveInv = {
            batchId: selectedBatch.batchId,
            fromDate: moveInvoices.fromDate,
            toDate: moveInvoices.toDate
        };

        var jsonItem = JSON.stringify(moveInv);

        const apiUrl = "MoveInvoices";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                var lockError = "";
                errors.map(function (error: any) {
                    toast.error(error.content);
                    lockError += error.content + " ";
                });
            } else {
                toast.success("Invoices have been moved to a new batch.");
                setMoveInvoices(prevState => ({ ...prevState, fromDate: "", toDate: "" }));
                getBatchNumbers(true, selectedBatch.batchId, showOpenBatches, sendEventInvoicesOnly);
            }
        };
    }
    //end move invoices into an open batch

    //lock the batch to stop invoices being added, before sending it to Reckon
    const lockBatch = () => {
        if (locking) {
            return;
        }
        setLocking(true);
        setMoveInvoices(prevState => ({ ...prevState, fromDate: "", toDate: "" }));
        setErrors({});

        lockInvoiceBatch();
    }

    const lockInvoiceBatch = async () => {
        var lockBatch = {
            batchId: selectedBatch.batchId
        };

        var jsonItem = JSON.stringify(lockBatch);

        const apiUrl = "LockBatch";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                var lockError = "";
                errors.map(function (error: any) {
                    toast.error(error.content);
                    lockError += error.content + " ";
                });
                setLocking(false);
                setLockBatchError(lockError);
            } else {
                toast.success("Invoice Batch has been Locked!");
                
                setShowOpenBatches(false);
                setLocking(false);
                getBatchNumbers(true, selectedBatch.batchId, false, sendEventInvoicesOnly);
            }
        };
    }

    const unlockBatch = () => {
        unlockInvoiceBatch();
    }

    const unlockInvoiceBatch = async () => {
        var lockBatch = {
            batchId: selectedBatch.batchId
        };

        var jsonItem = JSON.stringify(lockBatch);

        const apiUrl = "UnlockBatch";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                var lockError = "";
                errors.map(function (error: any) {
                    toast.error(error.content);
                    lockError += error.content + " ";
                });
                setLocking(false);
                setLockBatchError(lockError);
            } else {
                toast.success("Invoice Batch has been Unlocked!");

                setShowOpenBatches(false);
                setLocking(false);
                getBatchNumbers(true, selectedBatch.batchId, false, sendEventInvoicesOnly);
            }
        };
    }

    //send the batch to Reckon via API
    const sendInvoiceBatchToReckon = () => {
        if (sending) {
            return;
        }
        
        setSending(true);
        setBatchSending(true);
        setMoveInvoices(prevState => ({ ...prevState, fromDate: "", toDate: "" }));
        updateBatchStatusToSending();
    }

    const updateBatchStatusToSending = async () => {
        var sendToReckon = {
            batchId: selectedBatch.batchId,
            rowVersionTimestamp: rvt
        };

        var jsonItem = JSON.stringify(sendToReckon);

        const apiUrl = "UpdateBatchStatusToSending";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                var sendError = "";
                errors.map(function (error: any) {
                    toast.error(error.content);
                    sendError += error.content + " ";
                });
                setSendBatchError(sendError);
                setSending(false);
                setBatchSending(false);
                setInvoiceSending(false);
            } else {
                sendInvoiceBatchApi(result.data.rowVersionTimestamp);
            }
        };
    }

    const sendInvoiceBatchApi = async (updatedRvt: Uint8Array[]) => {
        var sendToReckon = {
            batchId: selectedBatch.batchId,
            rowVersionTimestamp: updatedRvt
        };

        var jsonItem = JSON.stringify(sendToReckon);

        const apiUrl = "SendInvoicesToReckonApi";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                var sendError = "";
                errors.map(function (error: any) {
                    toast.error(error.content);
                    sendError += error.content + " ";
                });
                setSendBatchError(sendError);
                setSending(false);
                setBatchSending(false);
                setInvoiceSending(false);
            } else {
                toast.success("Invoice Batch Sent To Reckon - check invoice status!");
                setSending(false);
                setBatchSending(false);
                setInvoiceSending(false);
                setShowOpenBatches(false);
                setSendBatchError("");
                getBatchNumbers(true, selectedBatch.batchId, false, sendEventInvoicesOnly);
            }
        };
    }

    var invoiceContents = <Invoices invoiceDetails={invoices} invoiceTotalFormatted={invoiceTotalFormatted} showCustType={false} showJobInfo={true} showQuoteNo={false} showHirePeriod={false} showStatus={true} showStartDate={selectedBatch.isEvent} showPaidDate={false} showPaid={false} showSend={tab == "Error"} showRemove={true} showSentTo={false} showReckonInvNo={true} showError={tab == "Error"} showSent={tab == "Error"} canEdit={selectedBatch.batchStatusId === 1 && tab === "Batch" && canEdit && !sending && !batchSending && !invoiceSending} canEditHirePeriod={false} canSend={tab == "Error"} canUpdatePaid={false} page={1} pageSize={totalRecords} totalRecords={totalRecords} pageChanged={noChange} beforeValid={updateInvoiceDetails} afterValid={save} saveHirePeriod={noChange} />

    const renderDetails = (
        <div>
            <div className="tabsComponent" id="tab-container">
                <div className="tabsComponent__showDropdown">
                    <label className="input-group" htmlFor="scheduleTab">
                        <span className="label">Invoices</span>
                        <select className="select" id="scheduleTab" value={tab} onChange={tabChanged} >
                            <option value="Batch">Batch</option>
                            <option value="Error">Error</option>
                        </select>
                    </label>
                </div>

                <div className="tabsComponent__tabs tabsComponent__hideTabs">
                    <input type="radio" name="tabs" id="tab1" className="tabsComponent__control" checked={tab == 'Batch'} onChange={(e) => noChange(e)}></input>
                    <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => tabSelected('Batch')}>Batch</label>
                    <input type="radio" name="tabs" id="tab2" className="tabsComponent__control" checked={tab == 'Error'} onChange={(e) => noChange(e)}></input>
                    <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => tabSelected('Error')}>Error</label>
                </div>

                <div className="tabsComponent__content tabsComponent__content--selected">
                    <div className={tab === "Batch" ? "" : "hidden"}>
                        <div className={batchSending ? "newJobPricelist--prepaid" : "hidden"}>A batch is being sent to Reckon. {sending ? "" : "Wait 5 mins and refresh the screen."}</div>
                        <div className={invoiceSending ? "newJobPricelist--prepaid" : "hidden"}>An invoice is being sent to Reckon. Wait 1 minute and refresh the screen.</div>
                        <div className="makeitflexspacebetween">
                            <span>
                                <label className="input-group--inline" htmlFor="batchId">
                                    <span className="label">Batch</span>
                                    <select className="select" id="batchId" name="batchId" value={selectedBatch.batchId} onChange={(e) => handleBatchChange(e)} disabled={sending || batchSending || invoiceSending}>
                                        <option defaultValue="-1"></option>
                                        {batchNumbers.map(batch =>
                                            <option key={batch.id} value={batch.id}>{batch.description}</option>
                                        )};
                                    </select>
                                </label>

                                <label className="input-group--inline marginLeft10" htmlFor="showOpenBatches">
                                    <span className="label">Show Open Only Batches</span>
                                    <input className="checkbox" type="checkbox" name="showOpenBatches" checked={showOpenBatches} onChange={(e) => handleShowOpenCheckboxChange(e)} disabled={sending || batchSending || invoiceSending}></input>
                                </label>

                                <label className={selectedBatch.sentDateFormatted === "" ? "hidden" : "input-group--inline marginLeft10"}>
                                    <span className="label">Sent Date:</span>
                                    <span>{selectedBatch.sentDateFormatted}</span>
                                </label>

                                <a className={selectedBatch.batchId < 1 || invoices.length < 1 || sendEventInvoicesOnly ? "hidden" : "marginLeft20 alignIconCenter"} title="Download Batch to Excel" href="#" onClick={(e) => downloadBatch(e)}>
                                    <i className="fa-solid fa-download edit--icon--large"></i>
                                </a>
                            </span>

                            <span className={selectedBatch.isEvent && selectedBatch.batchStatusId === 1 && invoices.length > 0 && !sending && !batchSending && !invoiceSending ? "alignitemscentre" : "hidden"}>
                                <span>Move invoices from </span>
                                <input type='date' id="fromDate" name="fromDate" className='input marginLeft10' value={moveInvoices.fromDate} onChange={(e) => handleMoveDateChange(e)}></input>
                                <span className="marginLeft10 marginRight10"> to </span>
                                <input type='date' id="toDate" name="toDate" className='input' value={moveInvoices.toDate} onChange={(e) => handleMoveDateChange(e)}></input>
                                <button className="defaultbutton defaultbutton__medium--height marginLeft10" type="button" onClick={moveInvoicesToNewBatch}>Move Invoices</button>
                            </span>
                        </div>
                        <div className="colAlignRight">
                            <span className={errors["fromDate"] ? "label errors " + (errors["toDate"] ? "marginRight30" : "batch-invoice-move--frommargin") : "hidden"}>{errors["fromDate"]}</span>
                            <span className={errors["toDate"] ? "label errors batch-invoice-move--tomargin" : "hidden"}>{errors["toDate"]}</span>
                        </div>
                        <div>
                            <button className={selectedBatch.batchStatusId === 1 && !loadingInvoices && invoices.length > 0 ? "defaultbutton marginBottom10" : "hidden"} type="button" onClick={lockBatch} disabled={locking || sending || batchSending || invoiceSending || eventBatchId == selectedBatch.batchId}>Lock Batch</button>
                            <button className={selectedBatch.batchStatusId === 4 && !loadingInvoices && invoices.length > 0 ? "defaultbutton marginBottom10" : "hidden"} type="button" onClick={unlockBatch} disabled={locking || sending || batchSending || invoiceSending}>Unlock Batch</button>
                            <span className={(lockBatchError || sendBatchError) && !sending ? "label errors" : "hidden"}>{lockBatchError} {sendBatchError}</span>
                        </div>

                        <div className={loadingInvoices ? "" : "hidden" }>
                            Loading...
                        </div>

                        <div className={loadingInvoices ? "hidden" : ""}>
                            {invoiceContents}
                        </div>
                        <div className={invoices.length > 0 ? "" : "hidden"}>
                            <button className="defaultbutton defaultbutton-next marginBottom10" type="button" onClick={sendInvoiceBatchToReckon} disabled={selectedBatch.batchStatusId != 4 || sending || batchSending || invoiceSending || loadingInvoices || eventBatchId == selectedBatch.batchId}>Send Batch To Reckon</button>
                            <span className={sendBatchError ? "label errors" : "hidden"}>{sendBatchError}</span>
                            <div className={sending ? "" : "hidden"}>Sending...</div>
                            <div></div>
                        </div>
                    </div>
                    <div className={tab === "Batch" ? "hidden" : ""}>
                        {invoiceContents}
                    </div>
                </div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default InvoiceBatchData;